<template>
  <div class="multi-uploader">
    <!-- 上传控件 -->
    <van-uploader
        v-model="fileList"
        multiple
        :max-count="maxCount"
        :max-size="maxSize * 1024 * 1024"
        :after-read="afterRead"
        @oversize="onOversize"
        @delete="onDelete"
        :deletable="deletable"
        upload-text="选择文件"
    >
      <template #preview-cover="{ file }">
        <div class="preview-cover">
          <span>{{ file.name }}</span>
          <span class="file-size">{{ formatFileSize(file.size) }}</span>
        </div>
      </template>
    </van-uploader>

    <!-- 上传状态显示 -->
    <div v-if="uploading" class="upload-status">
      <van-loading size="20px" vertical>上传中...</van-loading>
      <span>已上传 {{ uploadedCount }}/{{ fileList.length }}</span>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <van-button
          round
          type="primary"
          size="small"
          @click="handleUploadAll"
          :disabled="fileList.length === 0 || uploading"
      >
        全部上传
      </van-button>
      <van-button
          round
          type="danger"
          size="small"
          @click="handleClearAll"
          :disabled="fileList.length === 0 || uploading"
      >
        清空列表
      </van-button>
    </div>
  </div>
</template>

<script>
import { Uploader, Button, Loading, Toast, Dialog } from 'vant';
import {mapGetters} from "vuex";

export default {
  components: {
    [Uploader.name]: Uploader,
    [Button.name]: Button,
    [Loading.name]: Loading
  },
  props: {
    // 最大上传数量
    maxCount: {
      type: Number,
      default: 10
    },
    // 单个文件最大大小(MB)
    maxSize: {
      type: Number,
      default: 10
    },
    // 是否显示删除按钮
    deletable: {
      type: Boolean,
      default: true
    },
    packageId:{
      type:String,
      default: ''
    }
  },
  data() {
    return {
      fileList: [],
      uploading: false,
      uploadedCount: 0
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
  },
  watch:{
    packageId(val){
      console.log('packageId------------',val);
    }
  },
  methods: {
    loadResList(packageId){
      // debugger
      Toast.loading('加载中...');
      this.packageId = packageId;
      this.$http.get('/api/dlrUserResource/queryList', {
        params: {
          packageId
        }
      }).then(res => {
        this.fileList = this.transformResourceData(res.data);
      }).finally(() => {
        Toast.clear(true);
      });
    },
    transformResourceData(resources) {
      return resources.map(resource => ({
        uid: resource.id || Date.now().toString(), // 唯一标识
        name: resource.name || '未命名文件',
        url: resource.resUrl, // 文件访问URL
        status: 'success', // 标记为已上传成功
        message: '已上传',
        file: {
          name: resource.name || '未命名文件',
          size: resource.size || 0, // Make sure to include size if available
          type: resource.type || this.getFileTypeFromSuffix(resource.suffix) // Add type if needed
        },
        // 可以根据需要添加其他字段
        ...resource // 保留原始数据
      }));
    },
    // 文件读取完成后
    afterRead(file) {
      if (Array.isArray(file)) {
        // 多文件
        file.forEach(item => {
          this.prepareFile(item);
        });
      } else {
        // 单文件
        this.prepareFile(file);
      }
    },
    // 准备文件数据
    prepareFile(file) {
      file.status = 'ready';
      file.message = '等待上传';
      this.$set(file, 'uploadProgress', 0);
    },
    // 文件大小超出限制
    onOversize(file) {
      Toast.fail(`文件 ${file.name} 大小超过 ${this.maxSize}MB 限制`);
    },
    // 删除文件
    onDelete(file) {
      this.fileList = this.fileList.filter(item => item.uid !== file.uid);
    },
    getFileTypeFromSuffix(suffix) {
      if (!suffix) return 'unknown';

      const suffixLower = suffix.toLowerCase();

      // 图片类型
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      if (imageTypes.includes(suffixLower)) return 'image';

      // 视频类型
      const videoTypes = ['mp4', 'mov', 'avi', 'mkv', 'flv', 'wmv'];
      if (videoTypes.includes(suffixLower)) return 'video';

      // 文档类型
      const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];
      if (documentTypes.includes(suffixLower)) return 'document';

      // 音频类型
      const audioTypes = ['mp3', 'wav', 'aac', 'flac', 'ogg'];
      if (audioTypes.includes(suffixLower)) return 'audio';

      // 压缩包类型
      const archiveTypes = ['zip', 'rar', '7z', 'tar', 'gz'];
      if (archiveTypes.includes(suffixLower)) return 'archive';

      return 'other';
    },
    // 上传单个文件
    uploadFile(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        const fileDir = 'material/person/' + this.userInfo.id;
        formData.append('file', file.file);
        formData.append('dir', fileDir);

        const config = {
          onUploadProgress: progressEvent => {
            const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
            );
            file.uploadProgress = percent;
            file.status = 'uploading';
            file.message = `上传中 ${percent}%`;
          }
        };

        this.$http.post('/api/upload/uploadResource', formData, config)
            .then(response => {
              file.status = 'success';
              file.message = '上传成功';
              file.url = response.data.url; // 假设返回数据包含url
              resolve(response.data);
            })
            .catch(error => {
              file.status = 'failed';
              file.message = '上传失败';
              reject(error);
            });
      });
    },
    // 上传全部文件
    async handleUploadAll() {
      if (this.uploading) return;
      this.uploading = true;
      this.uploadedCount = 0;
      const successUrls = []; // 用于收集成功的URL
      try {
        const uploadPromises = this.fileList
            .filter(file => file.status !== 'success')
            .map(file => this.uploadFile(file)
                .then(response => {
                  this.uploadedCount++;
                  // 收集成功的URL
                  if (response) {
                    // successUrls.push(response);
                    successUrls.push({
                      ...response,
                      resUrl:response.url,
                      type: this.getFileTypeFromSuffix(response.suffix),
                      packageId: this.packageId // 为每个成功上传的对象添加packageId属性
                    });
                  }
                  return response; // 返回结果以便后续处理
                })
                .catch(error => {
                  console.error('上传失败:', error);
                  throw error; // 重新抛出错误以便外层捕获
                })
            );

        // 等待所有文件上传完成
        await Promise.all(uploadPromises);
        Toast.success(`上传完成 ${this.uploadedCount}/${this.fileList.length}`);

        // 如果有成功的URL，发送汇总请求
        if (successUrls.length > 0) {
          await this.sendResourceUrls(successUrls);
        }
      } catch (error) {
        console.error('批量上传出错:', error);
        Toast.fail('部分文件上传失败');
      } finally {
        this.uploading = false;
      }
    },
    // 新增方法：发送资源URL集合
    sendResourceUrls(resList) {
      return this.$http.post('/api/dlrUserResource/insert', resList).then(response => {
        Toast.success('资源提交成功');
        return response;
      }).catch(error => {
        Toast.fail('资源提交失败');
        throw error;
      });
    },
    // 清空文件列表
    handleClearAll() {
      Dialog.confirm({
        title: '确认清空',
        message: '确定要清空所有文件吗?'
      }).then(() => {
        this.$http.delete(`/api/dlrUserResource/clear?id=${this.packageId}`).then(()=>{
          this.fileList = [];
          Toast.success('已清空文件列表');
        })
      }).catch(() => {
        // 取消操作
      });
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style lang="less" scoped>
.multi-uploader {
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;

  /deep/ .van-uploader {
    width: 100%;

    &__upload {
      background-color: #f7f8fa;
      border: 1px dashed #ebedf0;
      border-radius: 4px;
    }

    &__preview {
      position: relative;
      margin: 0 8px 8px 0;
      border-radius: 4px;
      overflow: hidden;
    }

    &__preview-image {
      display: block;
    }

    &__file {
      position: relative;
      width: 80px;
      height: 80px;
      background-color: #f7f8fa;
      display: flex;
      align-items: center;
      justify-content: center;

      &-icon {
        font-size: 24px;
        color: #969799;
      }

      &-name {
        box-sizing: border-box;
        width: 100%;
        padding: 0 4px;
        font-size: 12px;
        text-align: center;
        color: #646566;
        word-break: break-all;
      }
    }

    &__mask {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #fff;
      background-color: rgba(0, 0, 0, 0.6);
      font-size: 12px;

      &-icon {
        margin-bottom: 4px;
        font-size: 20px;
      }
    }
  }

  .preview-cover {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 4px;
    font-size: 12px;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.5);

    .file-size {
      display: block;
      font-size: 10px;
      color: #eee;
    }
  }

  .upload-status {
    margin: 12px 0;
    text-align: center;
    font-size: 14px;
    color: #969799;

    .van-loading {
      margin-bottom: 8px;
    }
  }

  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 16px;

    .van-button {
      min-width: 100px;
    }
  }
}
</style>
